/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-06-29
 */

import React, { useEffect, useRef, useState } from "react";
import { useQueryListContext } from "./QueryListContext";
import { ErrorMessage } from "../../ErrorMessage";
import { SpinnerLoading } from "../../SpinnerLoading";
import { formatDate } from "./QueryList";
import { useAuth } from "../../../contexts/AuthContext";
import { CastType, DynamicObject, IColumnDetails, IQuery, IQueryColumnDetails, IWhere } from "../Interfaces";

interface FormField {
    label: string;
    value: string | number | boolean;
    type: "text" | "date"| "datetime-local" | "number" | "checkbox" | "select"; // Extendable for other input types
    options?: string[]; // For select type inputs
}
  
interface FormFields {
    [key: string]: FormField;
};
  
const initialFormFields: FormFields = {
    name: { label: "Name", value: "", type: "text" },
    age: { label: "Age", value: 0, type: "number" },
    gender: {label: "Gender", value: "Male", type: "select", options: ["Female", "Male", "Undefined"]},
    subscribed: { label: "Subscribed", value: false, type: "checkbox" },
};

export const Edit: React.FC = () => {

    // Context Data
    const {ctxData, getData, setData} = useQueryListContext();
    
    // Schema Columns
    const [schemaDataState, setSchemaDataState] = useState<IColumnDetails[] | undefined>();
    const [schemaControlState, setSchemaControlState] = useState<IColumnDetails[] | undefined>();

    const [controlRecordState, setControlRecordState] = useState<React.ReactElement[]>([]);
    const [dataRecordState, setDataRecordState] = useState<DynamicObject | null>(null);
    const [undefinedRecordState, setUndefinedRecordState] = useState<DynamicObject | null>(null);

    const [formFields, setFormFields] = useState<FormFields>(initialFormFields);

    // Page Support Hooks
    // Duplication - This needs to move out of here and into the context.
    const [isLoading, setIsLoading] = useState(false);
    const [httpError, setHttpError] = useState(null);

    const {isAuthenticated, token}  = useAuth();  

    // For Testing.
    useEffect(() => {

        setIsLoading(true);

        const endpoints: string[] = [getData("comms").schema, getData("comms").query];
        
        
        fetchEditRecordData(endpoints, processData).catch((error: any) => {
            setIsLoading(false);
            setHttpError(error.message);
        }).finally(() => {
            setIsLoading(false);
        });        

    }, [])

    const fetchEditRecordData = async (endpoints: string[], processData: any) => {
    
        const orginalQueryState: IQuery = getData("queryState");
        const editTableName: string = orginalQueryState.frontEndOnly === null ? '' : orginalQueryState.frontEndOnly.editTableName;
        
        const query: IQuery | null = createRecordQuery(
                                                        editTableName,
                                                        getData("primaryKeyColumn"), 
                                                        getData("primaryKeyValue"));

        const fullPathEndPoints: any[] = [];
    
        for(let e in endpoints) {
            fullPathEndPoints.push(
                fetch(endpoints[e],{
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(query)
                })
            );
        }
    
        const result = (
            await Promise.all(fullPathEndPoints)
        ).map((r) => r.json());
    
        const results: any[] = await Promise.all(result);
        

        const schema: IQueryColumnDetails[] = results[0];
        const data: DynamicObject = results[1].data[0].data;
        const control: DynamicObject = results[1].data[0].control;

        processData(schema, data);
        processControl(control);

    }

    const createRecordQuery = ( editTableName: string,
                                primaryKeyColumn: string,
                                primaryKeyValue: string):IQuery | null => {
        
        if(getData("primaryKeyValue") === "NEW RECORD") {
            return null;
        }

        const whereConstraints: IWhere[] = [];

        if(primaryKeyColumn.includes("|") && primaryKeyValue.includes("|")) {
            const keyColumns: string[] = primaryKeyColumn.split("|");
            const keyValues: string[] = primaryKeyValue.split("|");

            for(const i in keyColumns) {
                whereConstraints.push(
                    {
                        fullColumnName: keyColumns[i],
                        value: keyValues[i],
                        operator: "EQ",
                        cast: CastType.STRING
                    }                
                );
            }
        } else {
            whereConstraints.push(
                {
                    fullColumnName: primaryKeyColumn,
                    value: primaryKeyValue,
                    operator: "EQ",
                    cast: CastType.STRING
                }                
            );
        }

        const result: IQuery = {
            tableName: editTableName,
            select: [],
            groupBy: [],
            join: [],
            orderBy: [],
            where: whereConstraints,
            mainTableOnly: true,
            includeExtraColumns: true,
            includeControlColumns: true,
            modifiedAfter: null,
            touchedBefore: null,
            isDirty: null,
            pageNum: 0,
            pageSize: 1,
            frontEndOnly: null
        }

        //console.log(JSON.stringify(result));
        return result;
    }

    // Process the record for editing
    const processData = (schemaData: IQueryColumnDetails[], recordData: DynamicObject | null) => {
        
        const formFields: FormFields = {}
        schemaData.map((detail: IQueryColumnDetails) => {            

            // console.log(detail);
            const field: FormField = {
                label: detail.fullColumnName,
                value: (recordData !== null) ? recordData[detail.columnName] : '',
                type: "text"
            }
            // 'text' | 'number' | 'checkbox' | 'select';
            switch (detail.datatype) {
                case CastType.BIGINT:
                    field.type = "number"
                    break
                case CastType.BOOLEAN:
                    field.type = "checkbox"
                    break
                case CastType.DATE:
                    field.type = "date"
                    break
                case CastType.DECIMAL:
                    field.type = "number"
                    break
                case CastType.DOUBLE:
                    field.type = "number"
                    break   
                case CastType.FLOAT:
                    field.type = "number"
                    break
                case CastType.INTEGER:
                    field.type = "number"
                    break
                case CastType.JSONB:
                    field.type = "text"
                    break
                case CastType.NUMERIC:
                    field.type = "number"
                    break
                case CastType.STRING:
                    field.type = "text"
                    break
                case CastType.TIME:
                    field.type = "datetime-local"
                    break
                case CastType.UUID:
                    field.type = "text"
                    break                                                                                                                                            
                default:
                    field.type = "text"
              }
              formFields[detail.columnName] = field;
        });

        // console.log(formFields);

        setFormFields(formFields);
        setDataRecordState(recordData);
    }

    // Control Fields Display
    const processControl = (recordControl: DynamicObject | null) => {

        const controlFields: React.ReactElement[] = [];

        let i: number = 0;
        controlFields.push(
            <div key={i++} className="d-flex align-items-center gap-3">
                <span>
                    <b>Id : </b>[{recordControl !== null ? recordControl["id"] : ''}]
                </span>                        
            </div>                
        );
        controlFields.push(
            <div key={i++} className="d-flex align-items-center gap-3">
                <span>
                    <b>Created : </b>[{recordControl !== null ? formatDate(recordControl["created"] as string) : ''}]
                </span>                        
            </div>                
        );
        controlFields.push(
            <div key={i++} className="d-flex align-items-center gap-3">
                <span>
                    <b>Updated : </b>[{recordControl !== null ? formatDate(recordControl["updated"] as string) : ''}]
                </span>                        
            </div>  
        );        

        setControlRecordState(controlFields);
    }

    const handleInputChange = (key: string, newValue: string | number | boolean) => {
        setFormFields((prevFields) => ({
          ...prevFields,
          [key]: { ...prevFields[key], value: newValue },
        }));
    };
      
    const renderFormFields = () => {
        
        const leftColumn: React.ReactElement[] = [];
        const rightColumn: React.ReactElement[] = [];

        Object.keys(formFields).map((key, index) => {
            
            const field = formFields[key];
            switch (field.type) {
                case 'text':
                    const elementText: React.ReactElement = (
                        <div key={index} className="mb-3">
                            <label htmlFor={field.label} className="form-label ms-1 mb-1" style={{fontWeight:"500"}}>{field.label}:</label>
                            <div className="input-group">
                                <input
                                    id={field.label}
                                    name={field.label}
                                    type="text"
                                    value={field.value as string}
                                    className="form-control" 
                                    aria-label={field.label}
                                    onChange={(e) => handleInputChange(key, e.target.value)}
                                /> 
                            </div>                            
                        </div>                        
                    );
                    if (index % 2 === 0) {
                        leftColumn.push(elementText);
                    } else {
                        rightColumn.push(elementText);
                    }                     
                    break;
                case 'number':
                    const elementNumber: React.ReactElement = (
                        <div key={index} className="mb-3">
                            <label htmlFor={field.label} className="form-label ms-1 mb-1" style={{fontWeight:"500"}}>{field.label}:</label>
                            <div className="input-group">
                                <input
                                    id={field.label}
                                    name={field.label}
                                    type="number"
                                    value={field.value as number}
                                    className="form-control" 
                                    aria-label={field.label}                                    
                                    onChange={(e) => handleInputChange(key, parseInt(e.target.value))}
                                />
                            </div>
                        </div>
                    );
                    if (index % 2 === 0) {
                        leftColumn.push(elementNumber);
                    } else {
                        rightColumn.push(elementNumber);
                    }                     
                    break;
                case 'checkbox':
                    const elementCheckbox: React.ReactElement = (
                        <div key={index} className="form-check mb-3">
                            <br></br>
                            <label className="form-check-label" htmlFor={field.label}>{field.label}</label>
                            <input
                                id={field.label}
                                name={field.label}
                                type="checkbox"
                                checked={field.value as boolean}
                                className="form-check-input"
                                onChange={(e) => handleInputChange(key, e.target.checked)}
                            />
                            <br></br>
                        </div>
                    );
                    if (index % 2 === 0) {
                        leftColumn.push(elementCheckbox);
                    } else {
                        rightColumn.push(elementCheckbox);
                    }                     
                    break;
                case 'select':
                    const elementSelect: React.ReactElement = (
                        <div key={index}>
                            <label className="form-label ms-1 mb-1" style={{fontWeight:"500"}} htmlFor={field.label}>{field.label}:</label>
                            <select
                                id={field.label}
                                name={field.label}                                
                                value={field.value as string}
                                className="form-select mb-3"
                                onChange={(e) => handleInputChange(key, e.target.value)}
                            >
                                {field.options?.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>
                    );
                    if (index % 2 === 0) {
                        leftColumn.push(elementSelect);
                    } else {
                        rightColumn.push(elementSelect);
                    }                      
                    break;
                default:
                    return null;
            }
           
        });
        return (
            <div className="row">
                <div className="col-12 col-md-6">
                    {leftColumn}
                </div>
                <div className="col-12 col-md-6">
                    {rightColumn}
                </div>
            </div>            
        );         
    };

    // Get the Field and Values only and place inside the record Object
    const handleSave = (event: React.FormEvent) => {
        event.preventDefault();

        const record: DynamicObject = {}
        // Process formFields
        Object.keys(formFields).map((key) => {
            // console.log(key + " : " + formFields[key].value);

            if(formFields[key].type === "number") {
                if(formFields[key].value === "") {
                    record[key] = 0;
                }
            } else if(formFields[key].type === "checkbox") {
                if(formFields[key].value === "") {
                    record[key] = false;
                }
            } else {
                record[key] = Number(formFields[key].value);
            }
            
        });

        // console.log(JSON.stringify(record));
        saveRecord(record);
    };

    const saveRecord = async (record: DynamicObject) => {

        let saveUrl: string | null = null;

        if(getData("primaryKeyValue") === "NEW RECORD") {
            saveUrl = process.env.REACT_APP_BASE_URL +`/table/${getData("tableName")}/insertOrFail`;
        } else {
            saveUrl = process.env.REACT_APP_BASE_URL +`/table/${getData("tableName")}/updateOrFail`;
        }

        setIsLoading(true);

        // console.log("Record " + JSON.stringify(record));

        // Save record on server
        const response = await fetch(saveUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(record)
        }).catch((error: any) =>{
            setIsLoading(false);
            setHttpError(error.message);
        }).finally(() => {
            setIsLoading(false);
        })
    }

    // If set to true display our spinner.
    if(isLoading) {
        return(
            <SpinnerLoading />
        )
    }

    // If we have an error diplay it using our ErroMessage component.
    if(httpError) {
        return(
            <ErrorMessage error={httpError} />
        )
    }

    return(
        <>
            <div className="table-control-wrapper box-shadow d-flex mb-3 p-2">
                <div className="d-flex flex-wrap justify-content-between gap-3 w-100">
                    <div className="d-flex align-items-center gap-3">
                        <span>
                            <b>{getData("primaryKeyValue")}</b>
                        </span>                        
                    </div>
                    {controlRecordState}
                    <div className="d-flex align-items-center gap-3"> 
                        <button type="button" className="btn btn-secondary" onClick={handleSave}>Save</button>
                        <button type="button" className="btn btn-light" onClick={()=>{setData("primaryKeyValue", undefined);}}>Cancel</button>
                    </div>
                </div>
            </div>
            <form >
                {renderFormFields()}
            </form>            
        </>
    );
}

    // useEffect(() => {

    //     const fetchRecords = async () => {

    //         setIsLoading(true);

    //         const schemaData: IColumnDetails[] = getData("schemaData")
    //         setSchemaDataState(schemaData);

    //         const schemaControl: IColumnDetails[] = getData("schemaControl")
    //         setSchemaControlState(schemaControl);

    //         const query: IQuery | null = createRecordQuery(getData("tableName"), getData("primaryKeyColumn"), getData("primaryKeyValue"))

    //         // New Record
    //         if(query === null) {
    //             processData(null);
    //             processControl(null);

    //         } else { // Existing Record
    //             // Fetch data from server
    //             const response = await fetch(getData("queryUrl"), {
    //                 method: 'POST',
    //                 headers: {
    //                     'Content-Type': 'application/json',
    //                     'Authorization': `Basic ${encodedCredentials}`
    //                 },
    //                 body: JSON.stringify(query)
    //             });

    //             // If the response not Ok, then throw Invalid Response Status Exception.
    //             if(!response.ok) {
    //                 throw new Error("Invalid Response Status : " + response.status + " - " + response.statusText);
    //             } 

    //             // Get the reponse as json.
    //             const responseJson = await response.json();
    //             // Get specifically, the data element.
    //             const responseData = responseJson.data;

    //             for(const key in responseData) {
                    
    //                 const recordData: DynamicObject = responseData[key]["data"];
    //                 processData(recordData);

    //                 const recordControl: DynamicObject = responseData[key]["control"];
    //                 processControl(recordControl);

    //                 const recordUndefined: DynamicObject = responseData[key]["undefined"];
    //                 setUndefinedRecordState(recordUndefined);

    //                 // TODO
    //                 // Merge recordData and recordUndefined together
    //                 // const concatRecord = {...recordData, ...recordUndefined};
    //                 // console.log(JSON.stringify(concatRecord, null, 2));

    //                 break; // We only want one record
    //             }
    //         }
    //     }       
    //     // If all this never worked lets catch the error and display it.
    //     fetchRecords().catch((error: any) =>{
    //         setIsLoading(false);
    //         setHttpError(error.message);
    //     });
    //     fetchRecords().finally(() => {
    //         setIsLoading(false);
    //     });
    // }, [ctxData])